import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Settings, Users, Edit, Check, X, Building, Plus, Clock, UserCheck, UserX, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: "secretario", label: "Secretário(a)" },
  { value: "coordenador", label: "Coordenador(a) APS" },
  { value: "enfermeiro", label: "Enfermeiro(a)" },
  { value: "medico", label: "Médico(a)" },
  { value: "tecnico", label: "Técnico(a) Enf." },
  { value: "acs", label: "ACS" },
  { value: "especialista", label: "Especialista" },
  { value: "admin", label: "Administrador" },
];

const CATEGORY_LABELS = {
  enfermeiro: "Enfermeiro(a)", tecnico: "Técnico(a) de Enfermagem",
  acs: "ACS", medico: "Médico(a)", coordenador: "Coordenador(a) APS",
  secretario: "Secretário(a) de Saúde", especialista: "Especialista de Referência",
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [healthUnits, setHealthUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: "", is_active: true, health_unit_id: "" });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("validations");
  const [newUnit, setNewUnit] = useState({ name: "", address: "", unit_type: "UBS", municipality: "" });
  const [savingUnit, setSavingUnit] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(null); // user_id being rejected
  const [rejectReason, setRejectReason] = useState("");
  const [validating, setValidating] = useState(null);

  useEffect(() => {
    // /admin é exclusivo do administrador do sistema (role: admin)
    // Gestores municipais acessam /gestao-municipal
    if (user?.role !== "admin") {
      if (["secretario", "coordenador"].includes(user?.role)) {
        navigate("/gestao-municipal");
      } else {
        navigate("/dashboard");
      }
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersResp, pendingResp, unitsResp] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/pending-validations`, { withCredentials: true }),
        axios.get(`${API}/health-units`, { withCredentials: true }),
      ]);
      setUsers(usersResp.data || []);
      setPendingUsers(pendingResp.data || []);
      setHealthUnits(unitsResp.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleValidate = async (userId, status, reason = "") => {
    setValidating(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/validate`, { status, reason }, { withCredentials: true });
      setPendingUsers(prev => prev.filter(u => u.user_id !== userId));
      await fetchData();
      setRejectModal(null);
      setRejectReason("");
    } catch (e) { console.error(e); }
    finally { setValidating(null); }
  };

  const startEdit = (u) => {
    setEditingUser(u.user_id);
    setEditForm({ role: u.role, is_active: u.is_active, health_unit_id: u.health_unit_id || "" });
  };

  const handleSaveUser = async (userId) => {
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/admin/users/${userId}`, editForm, { withCredentials: true });
      setUsers(prev => prev.map(u => u.user_id === userId ? resp.data : u));
      setEditingUser(null);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleCreateUnit = async (e) => {
    e.preventDefault();
    setSavingUnit(true);
    try {
      const resp = await axios.post(`${API}/health-units`, newUnit, { withCredentials: true });
      setHealthUnits(prev => [...prev, resp.data]);
      setShowUnitModal(false);
      setNewUnit({ name: "", address: "", unit_type: "UBS", municipality: "" });
    } catch (e) { console.error(e); }
    finally { setSavingUnit(false); }
  };

  const getRoleLabel = (role) => ROLES.find(r => r.value === role)?.label || role;
  const getUnitName = (unitId) => healthUnits.find(u => u.unit_id === unitId)?.name || "";

  const TABS = [
    { id: "validations", label: "Validações Pendentes", badge: pendingUsers.length },
    { id: "users", label: "Usuários" },
    { id: "units", label: "Unidades de Saúde" },
  ];

  return (
    <Layout>
      <div data-testid="admin-page" className="space-y-5">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>Administração</h1>
          <p className="text-sm text-muted-foreground">Gestão de usuários, validações e unidades de saúde</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-secondary p-1 rounded-xl w-fit animate-fade-in flex-wrap">
          {TABS.map(({ id, label, badge }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                activeTab === id ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              {label}
              {badge > 0 && (
                <span className="bg-destructive text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-bold">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Validations tab */}
        {activeTab === "validations" && (
          <div className="space-y-3 animate-fade-in">
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map(i => <div key={i} className="h-24 rounded-xl bg-white border border-border animate-pulse" />)}
              </div>
            ) : pendingUsers.length === 0 ? (
              <div className="bg-white rounded-xl border border-border p-12 text-center">
                <UserCheck className="w-12 h-12 text-primary/30 mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground">Nenhum cadastro pendente</p>
                <p className="text-xs text-muted-foreground mt-1">Todos os profissionais foram validados</p>
              </div>
            ) : (
              pendingUsers.map(u => (
                <div key={u.user_id} className="bg-white rounded-xl border border-border p-4" data-testid={`pending-user-${u.user_id}`}>
                  <div className="flex items-start gap-3">
                    {u.picture ? (
                      <img src={u.picture} alt={u.full_name || u.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold flex-shrink-0">
                        {(u.full_name || u.name || "U")[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground">{u.full_name || u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                          {CATEGORY_LABELS[u.professional_category] || u.professional_category}
                        </span>
                        {u.council_number && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                            Reg: {u.council_number}
                          </span>
                        )}
                        {u.health_unit_id && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                            {getUnitName(u.health_unit_id) || "Unidade vinculada"}
                          </span>
                        )}
                        {u.phone && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                            {u.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleValidate(u.user_id, "aprovado")}
                        disabled={validating === u.user_id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
                        data-testid={`approve-user-${u.user_id}`}
                      >
                        <UserCheck className="w-3.5 h-3.5" />
                        Aprovar
                      </button>
                      <button
                        onClick={() => { setRejectModal(u.user_id); setRejectReason(""); }}
                        disabled={validating === u.user_id}
                        className="flex items-center gap-1 px-3 py-1.5 border border-destructive text-destructive rounded-lg text-xs font-medium hover:bg-destructive/5 transition-all disabled:opacity-60"
                        data-testid={`reject-user-${u.user_id}`}
                      >
                        <UserX className="w-3.5 h-3.5" />
                        Rejeitar
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Users tab */}
        {activeTab === "users" && (
          <div className="bg-white rounded-xl border border-border overflow-hidden animate-fade-in">
            {loading ? (
              <div className="p-8 space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />)}
              </div>
            ) : users.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Nenhum usuário cadastrado</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {users.map((u) => (
                  <div key={u.user_id} className="p-4" data-testid={`user-row-${u.user_id}`}>
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                          {u.name?.[0] || "U"}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground">{u.full_name || u.name}</p>
                          {!u.is_validated && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Pendente</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      </div>
                      {editingUser !== u.user_id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                            {getRoleLabel(u.role)}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                            {u.is_active ? "Ativo" : "Inativo"}
                          </span>
                          <button
                            onClick={() => startEdit(u)}
                            className="p-1.5 rounded-lg hover:bg-secondary transition-all"
                            data-testid={`edit-user-${u.user_id}`}
                          >
                            <Edit className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={editForm.role}
                            onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
                            className="h-8 text-xs border border-border rounded-lg px-2 bg-white"
                            data-testid="user-role-select"
                          >
                            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                          <select
                            value={editForm.health_unit_id}
                            onChange={e => setEditForm(p => ({ ...p, health_unit_id: e.target.value }))}
                            className="h-8 text-xs border border-border rounded-lg px-2 bg-white max-w-[120px]"
                          >
                            <option value="">Sem unidade</option>
                            {healthUnits.map(hu => <option key={hu.unit_id} value={hu.unit_id}>{hu.name}</option>)}
                          </select>
                          <label className="flex items-center gap-1 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editForm.is_active}
                              onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))}
                              className="accent-primary"
                            />
                            Ativo
                          </label>
                          <button
                            onClick={() => handleSaveUser(u.user_id)}
                            disabled={saving}
                            className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 transition-all"
                            data-testid={`save-user-${u.user_id}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="p-1.5 rounded-lg hover:bg-secondary transition-all"
                          >
                            <X className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Health units tab */}
        {activeTab === "units" && (
          <div className="space-y-3 animate-fade-in">
            <div className="flex justify-end">
              <button
                onClick={() => setShowUnitModal(true)}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-all"
                data-testid="new-health-unit-btn"
              >
                <Plus className="w-4 h-4" />
                Nova Unidade
              </button>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1,2].map(i => <div key={i} className="h-20 rounded-xl bg-white border border-border animate-pulse" />)}
              </div>
            ) : healthUnits.length === 0 ? (
              <div className="bg-white rounded-xl border border-border p-12 text-center">
                <Building className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Nenhuma unidade de saúde cadastrada</p>
              </div>
            ) : (
              healthUnits.map((unit) => (
                <div key={unit.unit_id} className="bg-white rounded-xl border border-border p-4" data-testid={`unit-row-${unit.unit_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{unit.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {unit.unit_type}{unit.municipality ? ` · ${unit.municipality}` : ""}
                        {unit.address ? ` · ${unit.address}` : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* New unit modal */}
      {showUnitModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4" onClick={e => e.target === e.currentTarget && setShowUnitModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-fade-in">
            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Nova Unidade de Saúde</h3>
            <form onSubmit={handleCreateUnit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome da Unidade *</label>
                <input required type="text" value={newUnit.name} onChange={e => setNewUnit(p => ({ ...p, name: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="unit-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tipo</label>
                <select value={newUnit.unit_type} onChange={e => setNewUnit(p => ({ ...p, unit_type: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30">
                  <option value="UBS">UBS</option>
                  <option value="UBSF">UBS de Família</option>
                  <option value="UPA">UPA</option>
                  <option value="Hospital">Hospital</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Município</label>
                <input type="text" value={newUnit.municipality} onChange={e => setNewUnit(p => ({ ...p, municipality: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Endereço</label>
                <input type="text" value={newUnit.address} onChange={e => setNewUnit(p => ({ ...p, address: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowUnitModal(false)} className="flex-1 py-3 border border-border rounded-xl text-sm">Cancelar</button>
                <button type="submit" disabled={savingUnit} className="flex-1 py-3 bg-primary text-white rounded-xl text-sm font-medium disabled:opacity-60" data-testid="save-unit-btn">
                  {savingUnit ? "Salvando..." : "Criar Unidade"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject reason modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4" onClick={e => e.target === e.currentTarget && setRejectModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <h3 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>Rejeitar Cadastro</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Informe o motivo da rejeição. O profissional será notificado.</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Ex: Documentação incompleta, número de registro inválido..."
              rows={3}
              className="w-full border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-destructive/30 resize-none"
              data-testid="reject-reason-input"
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setRejectModal(null)} className="flex-1 py-3 border border-border rounded-xl text-sm">Cancelar</button>
              <button
                onClick={() => handleValidate(rejectModal, "rejeitado", rejectReason)}
                disabled={validating === rejectModal}
                className="flex-1 py-3 bg-destructive text-white rounded-xl text-sm font-medium disabled:opacity-60"
                data-testid="confirm-reject-btn"
              >
                {validating === rejectModal ? "Rejeitando..." : "Confirmar Rejeição"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
